// src/components/AdminSettings/AdminSettings.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Save,
  AlertCircle,
  CheckCircle,
  User,
  Mail,
  MapPin,
  Globe,
  BookOpen,
  Award,
  Hash,
  Briefcase,
  Lock
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import { showToast } from '../../utils/toast';

const AdminSettings = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    prefix: '',
    first_name: '',
    last_name: '',
    other_name: '',
    email: '',
    affiliation: '',
    affiliation_country: '',
    affiliation_city: '',
    orcid: '',
    asfi_membership_id: '',
    discipline: '',
    is_available_for_review: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // Load user data
  useEffect(() => {
    // In real app, this would fetch from API
    // For now, populate with mock data
    setFormData({
          prefix: user?.prefix || '',
          first_name: user?.firstName || user?.firstname || '',
          last_name: user?.lastName || user?.lastname || '',
          other_name: user?.otherName || user?.othername || '',
          email: user?.email || '',
          affiliation: user?.affiliations || user?.affiliation || '',
          affiliation_country: user?.affiliationCountry || user?.affiliation_country || '',
          affiliation_city: user?.affiliationCity || user?.affiliation_city || '',
          orcid: user?.orcidId || user?.orcid || '',
          asfi_membership_id: user?.asfiMembershipId || user?.asfi_membership_id || '',
          discipline: user?.discipline || '',
          is_available_for_review: user?.isAvailableForReview || user?.is_available_for_review || 'no'
    });
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update user context
      updateUser({
        firstName: formData.first_name,
        lastName: formData.last_name,
        email: formData.email
      });

      showToast('Your account information has been updated successfully.', 'success');
    } catch (error) {
      showToast('Failed to update account information. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      showToast('New passwords do not match.', 'error');
      return;
    }

    if (passwordData.new_password.length < 8) {
      showToast('Password must be at least 8 characters long.', 'error');
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      showToast('Your password has been changed successfully.', 'success');

      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setShowPasswordForm(false);
    } catch (error) {
      showToast('Failed to change password. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const disciplines = [
    'Agricultural sciences',
    'Allied Health Sciences',
    'Anthropology',
    'Arts',
    'Biology',
    'Chemistry',
    'Climate Change',
    'Computer and Information Sciences',
    'Earth Sciences',
    'Engineering and Technology',
    'Environmental Sciences',
    'Epidemiology',
    'Humanities',
    'Leadership and Management',
    'Life Sciences',
    'Materials Science',
    'Mathematics',
    'Medicine',
    'Physics and Astronomy',
    'Politics',
    'Public & Global Health',
    'Social and Behavioral Sciences',
    'Statistics',
    'Theology',
    'Other'
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[--asfi-purple] flex items-center">
          <User className="w-6 h-6 mr-3" />
          Account Settings
        </h1>
        <p className="text-gray-600 mt-1">Manage your account information and preferences</p>
      </div>

      {/* Account Information Form */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <User className="w-5 h-5 mr-2 text-[--asfi-purple]" />
            Personal Information
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Prefix */}
            <div>
              <label htmlFor="prefix" className="block text-sm font-medium text-gray-700 mb-1">
                Prefix <span className="text-red-500">*</span>
              </label>
              <select
                id="prefix"
                name="prefix"
                value={formData.prefix}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
              >
                <option value="">Select Preferred Prefix</option>
                <option value="Prof.">Prof.</option>
                <option value="Dr.">Dr.</option>
                <option value="Mr.">Mr.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Miss">Miss</option>
              </select>
            </div>

            {/* First Name */}
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
              />
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
              />
            </div>

            {/* Other Name */}
            <div>
              <label htmlFor="other_name" className="block text-sm font-medium text-gray-700 mb-1">
                Other Name
              </label>
              <input
                type="text"
                id="other_name"
                name="other_name"
                value={formData.other_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
              />
            </div>

            {/* Email */}
            <div className="md:col-span-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                readOnly
                className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
            </div>
          </div>

          <div className="border-t border-gray-200 my-6"></div>

          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Briefcase className="w-5 h-5 mr-2 text-[--asfi-purple]" />
            Affiliation Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Affiliation */}
            <div className="md:col-span-3">
              <label htmlFor="affiliation" className="block text-sm font-medium text-gray-700 mb-1">
                Affiliation/Institution <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="affiliation"
                name="affiliation"
                value={formData.affiliation}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                placeholder="e.g., University of Lagos"
              />
            </div>

            {/* Country */}
            <div>
              <label htmlFor="affiliation_country" className="block text-sm font-medium text-gray-700 mb-1">
                Country <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="affiliation_country"
                name="affiliation_country"
                value={formData.affiliation_country}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                placeholder="e.g., Nigeria"
              />
            </div>

            {/* City */}
            <div>
              <label htmlFor="affiliation_city" className="block text-sm font-medium text-gray-700 mb-1">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="affiliation_city"
                name="affiliation_city"
                value={formData.affiliation_city}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                placeholder="e.g., Lagos"
              />
            </div>
          </div>

          <div className="border-t border-gray-200 my-6"></div>

          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Award className="w-5 h-5 mr-2 text-[--asfi-purple]" />
            Professional Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ORCID ID */}
            <div>
              <label htmlFor="orcid" className="block text-sm font-medium text-gray-700 mb-1">
                ORCID ID
              </label>
              <input
                type="text"
                id="orcid"
                name="orcid"
                value={formData.orcid}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                placeholder="0000-0002-3458-8696"
              />
              <p className="mt-1 text-xs text-gray-500">Format: XXXX-XXXX-XXXX-XXXX</p>
            </div>

            {/* ASFI Membership ID */}
            <div>
              <label htmlFor="asfi_membership_id" className="block text-sm font-medium text-gray-700 mb-1">
                ASFI Membership ID
              </label>
              <input
                type="text"
                id="asfi_membership_id"
                name="asfi_membership_id"
                value={formData.asfi_membership_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                placeholder="ASFI-2024-XXXX"
              />
            </div>

            {/* Discipline */}
            <div className="md:col-span-2">
              <label htmlFor="discipline" className="block text-sm font-medium text-gray-700 mb-1">
                Discipline <span className="text-red-500">*</span>
              </label>
              <select
                id="discipline"
                name="discipline"
                value={formData.discipline}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
              >
                <option value="">Select a Discipline</option>
                {disciplines.map(discipline => (
                  <option key={discipline} value={discipline}>{discipline}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="border-t border-gray-200 my-6"></div>

          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <BookOpen className="w-5 h-5 mr-2 text-[--asfi-purple]" />
            Review Preferences
          </h3>

          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-3">Are you available to review for ASFIRJ? (optional)</p>
            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="is_available_for_review"
                  value="yes"
                  checked={formData.is_available_for_review === 'yes'}
                  onChange={handleChange}
                  className="w-4 h-4 text-[--asfi-purple] focus:ring-[--asfi-purple] mr-2"
                />
                <span className="text-sm text-gray-700">Yes</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="is_available_for_review"
                  value="no"
                  checked={formData.is_available_for_review === 'no'}
                  onChange={handleChange}
                  className="w-4 h-4 text-[--asfi-purple] focus:ring-[--asfi-purple] mr-2"
                />
                <span className="text-sm text-gray-700">No</span>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Change Password Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <Lock className="w-5 h-5 mr-2 text-[--asfi-purple]" />
            Password
          </h2>
          <button
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="text-sm text-[--asfi-purple] hover:text-[--asfi-blue] font-medium"
          >
            {showPasswordForm ? 'Cancel' : 'Change Password'}
          </button>
        </div>

        {showPasswordForm && (
          <form onSubmit={handlePasswordSubmit} className="p-6">
            <div className="space-y-4 max-w-md">
              <div>
                <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  id="current_password"
                  name="current_password"
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  id="new_password"
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
              </div>

              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirm_password"
                  name="confirm_password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                />
              </div>

              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;